import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUp, ChevronDown, X, CaseSensitive, Regex } from 'lucide-react';
import type { Editor } from '@tiptap/react';
import { findMatches } from '../../../utils/findReplace';
import { applySearchHighlight, clearSearchHighlight, replaceMatches, scrollToMatch } from '../editorSearch';
import '../styles/findReplace.css';

interface FindReplacePanelProps {
  editor: Editor;
  onClose: () => void;
}

/**
 * 章节内查找替换面板。
 *
 * 补上此前缺失的能力：编辑器把 Ctrl+F 劫持为"聚焦全局搜索"（只读），
 * 却没有提供任何查找替换，改人名/术语只能手工逐处修改。
 * 全书范围的检索仍走 Ctrl+Shift+F（全局搜索）。
 */
export function FindReplacePanel({ editor, onClose }: FindReplacePanelProps) {
  const [query, setQuery] = useState('');
  const [replacement, setReplacement] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [regex, setRegex] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showReplace, setShowReplace] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const text = editor.getText();
  const { matches, error } = useMemo(
    () => findMatches(text, query, { caseSensitive, regex }),
    [text, query, caseSensitive, regex],
  );

  // 命中集合变化后回到第一处，避免索引越界
  useEffect(() => {
    setActiveIndex(0);
  }, [query, caseSensitive, regex]);

  // 同步高亮 + 滚动到当前命中
  useEffect(() => {
    if (!query || matches.length === 0) {
      clearSearchHighlight(editor);
      return;
    }
    const index = Math.min(activeIndex, matches.length - 1);
    applySearchHighlight(editor, { query, matches, activeIndex: index, caseSensitive, regex });
    scrollToMatch(editor, matches[index]);
  }, [editor, query, matches, activeIndex, caseSensitive, regex]);

  // 卸载时清掉高亮
  useEffect(() => () => clearSearchHighlight(editor), [editor]);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Esc 全局关闭：替换后焦点会回到编辑器，此时面板的 onKeyDown 收不到按键，
  // 只靠面板内监听会导致"按 Esc 关不掉"。这里在 window 上兜底。
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const goto = useCallback((delta: number) => {
    if (matches.length === 0) return;
    setActiveIndex(prev => (prev + delta + matches.length) % matches.length);
  }, [matches.length]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      // 有替换内容时 Enter 执行替换，否则跳转下一处（与常见编辑器一致）
      if (showReplace && replacement) handleReplace();
      else goto(e.shiftKey ? -1 : 1);
    }
  };

  const handleReplace = () => {
    if (matches.length === 0) return;
    const applied = replaceMatches(editor, query, { caseSensitive, regex }, replacement, false);
    // 替换后命中集合已变（少了一处），回到第一处继续找；
    // 若沿用旧索引会出现"跳到中间"的错觉（例如显示 2/2 而不是 1/2）
    if (applied > 0) setActiveIndex(0);
  };

  const handleReplaceAll = () => {
    if (matches.length === 0) return;
    replaceMatches(editor, query, { caseSensitive, regex }, replacement, true);
    setActiveIndex(0);
  };

  return (
    <div className="find-replace-panel" role="dialog" aria-label="查找替换" onKeyDown={handleKeyDown}>
      <div className="find-replace-row">
        <button
          className="find-replace-toggle"
          onClick={() => setShowReplace(v => !v)}
          title={showReplace ? '隐藏替换' : '显示替换'}
          aria-label={showReplace ? '隐藏替换' : '显示替换'}
        >
          {showReplace ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
        </button>
        <input
          ref={inputRef}
          className="find-replace-input"
          placeholder="查找"
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label="查找内容"
        />
        <span className={`find-replace-count${error ? ' is-error' : ''}`}>
          {error ? error : (query ? (matches.length === 0 ? '无结果' : `${Math.min(activeIndex, matches.length - 1) + 1}/${matches.length}`) : '')}
        </span>
        <button
          className={`find-replace-flag${caseSensitive ? ' is-active' : ''}`}
          onClick={() => setCaseSensitive(v => !v)}
          title="区分大小写"
          aria-pressed={caseSensitive}
        >
          <CaseSensitive size={14} />
        </button>
        <button
          className={`find-replace-flag${regex ? ' is-active' : ''}`}
          onClick={() => setRegex(v => !v)}
          title="正则表达式"
          aria-pressed={regex}
        >
          <Regex size={14} />
        </button>
        <button className="find-replace-nav" onClick={() => goto(-1)} disabled={matches.length === 0} title="上一处（Shift+Enter）" aria-label="上一处">
          <ChevronUp size={14} />
        </button>
        <button className="find-replace-nav" onClick={() => goto(1)} disabled={matches.length === 0} title="下一处（Enter）" aria-label="下一处">
          <ChevronDown size={14} />
        </button>
        <button className="find-replace-nav" onClick={onClose} title="关闭（Esc）" aria-label="关闭">
          <X size={14} />
        </button>
      </div>

      {showReplace && (
        <div className="find-replace-row">
          <span className="find-replace-spacer" />
          <input
            className="find-replace-input"
            placeholder="替换为"
            value={replacement}
            onChange={e => setReplacement(e.target.value)}
            aria-label="替换内容"
          />
          <button className="find-replace-action" onClick={handleReplace} disabled={matches.length === 0}>替换</button>
          <button className="find-replace-action" onClick={handleReplaceAll} disabled={matches.length === 0}>全部替换</button>
        </div>
      )}

      {regex && (
        <div className="find-replace-hint">正则模式：替换串可用 $1 引用捕获组</div>
      )}
    </div>
  );
}
